/**
 * PTEL Andalucía - Detector de Coordenadas Y Truncadas v1.0
 * 
 * Detecta y corrige coordenadas Y con el dígito "4" inicial truncado,
 * un patrón común en documentos PTEL municipales andaluces.
 * 
 * Problema real detectado:
 * - Íllar: 4093163 → 093163 (falta el "4")
 * - Tíjola: 4143256 → 143256
 * - Quéntar: 4120847 → 120847
 * - Berja: 4085432 → 085432
 * 
 * Rango válido Y para Andalucía EPSG:25830: 4.050.000 - 4.290.000
 * 
 * @version 1.0.0
 * @date 28-Dic-2025
 */

import { 
  getCentroidePorINE, 
  distanciaUTM
} from '../CoordinateValidator';

// ============================================================================
// CONSTANTES
// ============================================================================

/** Rango válido de coordenadas Y para Andalucía en EPSG:25830 */
export const ANDALUCIA_Y_MIN = 4050000;
export const ANDALUCIA_Y_MAX = 4290000;

/** Umbral para detectar Y potencialmente truncada (menor a 1 millón) */
export const Y_TRUNCATION_THRESHOLD = 1000000;

/** Radio máximo para validación contra centroide (km) */
export const DEFAULT_VALIDATION_RADIUS_KM = 50;

/**
 * Códigos de provincia andaluza (primeros 2 dígitos del INE)
 */
export const CODIGO_PROVINCIA = {
  ALMERIA: '04',
  CADIZ: '11',
  CORDOBA: '14',
  GRANADA: '18',
  HUELVA: '21',
  JAEN: '23',
  MALAGA: '29',
  SEVILLA: '41',
} as const;

export type CodigoProvincia = typeof CODIGO_PROVINCIA[keyof typeof CODIGO_PROVINCIA];

/**
 * Umbrales de validación por provincia (km)
 * Adaptados a la extensión y orografía de cada provincia
 */
export const UMBRALES_PROVINCIA: Record<CodigoProvincia, number> = {
  '04': 40, // Almería - municipios muy extensos (Los Vélez, Tabernas)
  '18': 35, // Granada - orografía montañosa (Alpujarras, Sierra Nevada)
  '11': 30, // Cádiz
  '14': 30, // Córdoba
  '21': 30, // Huelva
  '23': 30, // Jaén
  '29': 30, // Málaga
  '41': 30, // Sevilla
};

/** Umbral por defecto para provincias no reconocidas */
export const UMBRAL_DEFAULT_KM = 30;

// ============================================================================
// TIPOS E INTERFACES
// ============================================================================

export type TruncationMethod = 'prefix_4' | 'centroid_validation' | 'none';

export interface TruncationResult {
  /** Indica si la coordenada Y está truncada */
  isTruncated: boolean;
  /** Valor original de la coordenada Y */
  originalValue: number;
  /** Valor corregido (si se pudo corregir) */
  correctedValue?: number;
  /** Confianza de la corrección (0-100) */
  confidence: number;
  /** Método utilizado para la corrección */
  method: TruncationMethod;
  /** Descripción del proceso aplicado */
  description: string;
}

export interface CentroidValidationResult {
  /** Si la coordenada corregida es válida geográficamente */
  isValid: boolean;
  /** Distancia al centroide municipal en km */
  distanceKm: number;
  /** Si está dentro del radio de validación */
  withinRadius: boolean;
}

// ============================================================================
// FUNCIONES PRINCIPALES
// ============================================================================

/**
 * Detecta si una coordenada Y está truncada (falta el dígito "4" inicial)
 * 
 * Una Y se considera potencialmente truncada si:
 * 1. Es menor a 1.000.000 (umbral de truncación)
 * 2. Al añadir el prefijo "4", cae dentro del rango válido de Andalucía
 * 
 * @param y - Coordenada Y a verificar
 * @param codigoINE - Código INE del municipio (opcional, para validación adicional)
 * @returns Resultado de la detección con corrección propuesta
 * 
 * @example
 * // Coordenada truncada de Íllar
 * detectTruncation(93163)  // { isTruncated: true, correctedValue: 4093163, ... }
 * 
 * // Coordenada válida
 * detectTruncation(4093163)  // { isTruncated: false, ... }
 */
export function detectTruncation(y: number, codigoINE?: string): TruncationResult {
  // Caso 1: Y ya está en rango válido - no truncada
  if (y >= ANDALUCIA_Y_MIN && y <= ANDALUCIA_Y_MAX) {
    return {
      isTruncated: false,
      originalValue: y,
      confidence: 100,
      method: 'none',
      description: 'Coordenada Y dentro del rango válido de Andalucía',
    };
  }
  
  // Caso 2: Y menor al umbral - potencialmente truncada
  if (y < Y_TRUNCATION_THRESHOLD) {
    // Intentar corrección añadiendo prefijo "4"
    const correctedY = addPrefix4(y);
    
    // Verificar si la corrección cae en rango válido
    if (correctedY >= ANDALUCIA_Y_MIN && correctedY <= ANDALUCIA_Y_MAX) {
      let confidence = 85; // Confianza base por corrección geométrica
      
      // Si tenemos código INE, validar contra centroide
      if (codigoINE) {
        const centroidValidation = getCentroidValidationConfidence(correctedY, codigoINE);
        if (centroidValidation > 0) {
          confidence = centroidValidation;
        }
      }
      
      return {
        isTruncated: true,
        originalValue: y,
        correctedValue: correctedY,
        confidence,
        method: codigoINE ? 'centroid_validation' : 'prefix_4',
        description: `Y truncada corregida: ${y} → ${correctedY} (añadido prefijo "4")`,
      };
    }
  }
  
  // Caso 3: Y fuera de rango pero no corregible con prefijo "4"
  return {
    isTruncated: false,
    originalValue: y,
    confidence: 0,
    method: 'none',
    description: `Coordenada Y=${y} fuera de rango y no corregible con prefijo "4"`,
  };
}

/**
 * Corrige una coordenada Y truncada añadiendo el prefijo "4"
 * 
 * Solo corrige si:
 * 1. La Y es menor al umbral de truncación
 * 2. El resultado cae dentro del rango válido de Andalucía
 * 
 * @param y - Coordenada Y truncada
 * @param _provincia - Provincia (reservado para validación futura)
 * @returns Coordenada Y corregida o la original si no aplica
 * 
 * @example
 * correctTruncation(93163, 'Almería')   // 4093163
 * correctTruncation(4093163, 'Almería') // 4093163 (sin cambios)
 */
export function correctTruncation(y: number, _provincia?: string): number {
  // Si ya está en rango válido, devolver sin cambios
  if (y >= ANDALUCIA_Y_MIN && y <= ANDALUCIA_Y_MAX) {
    return y;
  }
  
  // Si es menor al umbral, intentar corrección
  if (y < Y_TRUNCATION_THRESHOLD) {
    const correctedY = addPrefix4(y);
    
    // Solo devolver corregida si cae en rango válido
    if (correctedY >= ANDALUCIA_Y_MIN && correctedY <= ANDALUCIA_Y_MAX) {
      return correctedY;
    }
  }
  
  // No se pudo corregir, devolver original
  return y;
}

/**
 * Valida una coordenada corregida contra el centroide municipal
 * 
 * @param x - Coordenada X UTM
 * @param y - Coordenada Y UTM (ya corregida)
 * @param codigoINE - Código INE del municipio (5 dígitos)
 * @param radiusKm - Radio de tolerancia en km (default: 50km)
 * @returns Resultado de la validación geográfica
 * 
 * @example
 * // Validar coordenada de Íllar (04053)
 * validateAgainstCentroid(522000, 4093163, '04053')
 * // { isValid: true, distanceKm: 2.3, withinRadius: true }
 */
export function validateAgainstCentroid(
  x: number, 
  y: number, 
  codigoINE: string,
  radiusKm: number = DEFAULT_VALIDATION_RADIUS_KM
): CentroidValidationResult {
  const centroide = getCentroidePorINE(codigoINE);
  
  if (!centroide) {
    return {
      isValid: false,
      distanceKm: -1,
      withinRadius: false,
    };
  }
  
  const distanceKm = distanciaUTM(x, y, centroide.x, centroide.y);
  const withinRadius = distanceKm <= radiusKm;
  
  return {
    isValid: withinRadius,
    distanceKm: Math.round(distanceKm * 100) / 100,
    withinRadius,
  };
}

// ============================================================================
// FUNCIONES AUXILIARES
// ============================================================================

/**
 * Añade el prefijo "4" a una coordenada Y truncada
 * 
 * Maneja diferentes longitudes de dígitos:
 * - 5 dígitos (93163) → 4093163
 * - 6 dígitos (143256) → 4143256
 * 
 * @param y - Coordenada Y truncada
 * @returns Coordenada Y con prefijo "4" añadido
 */
function addPrefix4(y: number): number {
  const yStr = y.toString();
  const digits = yStr.length;
  
  // Determinar cuántos ceros añadir para completar 7 dígitos
  // Y válida en Andalucía: 4xxxxxx (7 dígitos)
  if (digits <= 6) {
    // Construir número con prefijo 4 y ceros necesarios
    const zeros = 6 - digits;
    const paddedY = '4' + '0'.repeat(zeros) + yStr;
    return parseInt(paddedY, 10);
  }
  
  // Si ya tiene 7+ dígitos, simplemente añadir 4 al inicio
  return parseInt('4' + yStr, 10);
}

/**
 * Calcula la confianza de una corrección basándose en la distancia 2D al centroide
 * 
 * MEJORA A03: Usa validateCorrectionConfidence() con distancia euclidiana 2D
 * en lugar de solo diferencia en Y. Cuando no tenemos X, usamos la X del
 * centroide como aproximación razonable.
 * 
 * @param correctedY - Coordenada Y corregida
 * @param codigoINE - Código INE del municipio
 * @returns Confianza (0-100) o -1 si no hay centroide disponible
 */
function getCentroidValidationConfidence(correctedY: number, codigoINE: string): number {
  const centroide = getCentroidePorINE(codigoINE);
  if (!centroide) return -1;
  
  // A03: Usar X del centroide como aproximación para validación 2D
  // Esto es más preciso que solo comparar Y
  const validation = validateCorrectionConfidence(centroide.x, correctedY, codigoINE);
  
  // Si la corrección es rechazada (fuera del umbral provincial), retornar score bajo
  if (validation.level === 'RECHAZADA') {
    return Math.max(0, validation.score);
  }
  
  return validation.score;
}

// ============================================================================
// A02: HEURÍSTICA CORRECCIÓN POR CENTROIDE MUNICIPAL
// ============================================================================

/**
 * Extrae el código de provincia de un código INE municipal
 * 
 * @param codigoINE - Código INE del municipio (5 dígitos, ej: "04053")
 * @returns Código de provincia (2 dígitos) o null si inválido
 * 
 * @example
 * detectProvinciaFromINE('04053') // '04' (Almería)
 * detectProvinciaFromINE('18051') // '18' (Granada)
 */
export function detectProvinciaFromINE(codigoINE: string): CodigoProvincia | null {
  if (!codigoINE || codigoINE.length < 2) return null;
  const provincia = codigoINE.substring(0, 2);
  
  // Verificar que es una provincia andaluza válida
  const provinciasValidas = Object.values(CODIGO_PROVINCIA);
  if (provinciasValidas.includes(provincia as CodigoProvincia)) {
    return provincia as CodigoProvincia;
  }
  return null;
}

/**
 * Obtiene el umbral de tolerancia (km) para una provincia
 * 
 * @param codigoINE - Código INE del municipio
 * @returns Radio de tolerancia en km (adaptado a la provincia)
 */
export function getProvincialThreshold(codigoINE: string): number {
  const provincia = detectProvinciaFromINE(codigoINE);
  if (!provincia) return UMBRAL_DEFAULT_KM;
  return UMBRALES_PROVINCIA[provincia] ?? UMBRAL_DEFAULT_KM;
}

export interface CorrectionConfidenceResult {
  /** Score de confianza 0-100 */
  score: number;
  /** Distancia al centroide en km */
  distanceKm: number;
  /** Si está dentro del umbral provincial */
  withinThreshold: boolean;
  /** Umbral usado (km) */
  thresholdKm: number;
  /** Provincia detectada */
  provincia: CodigoProvincia | null;
  /** Nivel de confianza descriptivo */
  level: 'ALTA' | 'MEDIA' | 'BAJA' | 'RECHAZADA';
}

/**
 * Valida la confianza de una corrección de coordenadas usando distancia 2D al centroide
 * 
 * Score no lineal:
 * - ≤5km: 95-100
 * - ≤10km: 85-94
 * - ≤15km: 75-84
 * - ≤umbral: 50-74
 * - >umbral: 0-49 (rechazada)
 * 
 * @param x - Coordenada X UTM corregida
 * @param y - Coordenada Y UTM corregida
 * @param codigoINE - Código INE del municipio (5 dígitos)
 * @returns Resultado de validación con score y metadata
 * 
 * @example
 * // Íllar - coordenada válida cerca del centroide
 * validateCorrectionConfidence(522000, 4093163, '04053')
 * // { score: 92, distanceKm: 3.5, withinThreshold: true, level: 'ALTA' }
 */
export function validateCorrectionConfidence(
  x: number,
  y: number,
  codigoINE: string
): CorrectionConfidenceResult {
  const provincia = detectProvinciaFromINE(codigoINE);
  const thresholdKm = getProvincialThreshold(codigoINE);
  
  const centroide = getCentroidePorINE(codigoINE);
  if (!centroide) {
    return {
      score: 0,
      distanceKm: -1,
      withinThreshold: false,
      thresholdKm,
      provincia,
      level: 'RECHAZADA',
    };
  }
  
  // Distancia euclidiana 2D en km
  const distanceKm = distanciaUTM(x, y, centroide.x, centroide.y);
  const withinThreshold = distanceKm <= thresholdKm;
  
  // Cálculo de score no lineal
  let score: number;
  let level: 'ALTA' | 'MEDIA' | 'BAJA' | 'RECHAZADA';
  
  if (distanceKm <= 5) {
    // Muy cerca: 95-100 (interpolación lineal)
    score = 100 - (distanceKm / 5) * 5;
    level = 'ALTA';
  } else if (distanceKm <= 10) {
    // Cerca: 85-94
    score = 94 - ((distanceKm - 5) / 5) * 9;
    level = 'ALTA';
  } else if (distanceKm <= 15) {
    // Moderada: 75-84
    score = 84 - ((distanceKm - 10) / 5) * 9;
    level = 'MEDIA';
  } else if (withinThreshold) {
    // Dentro del umbral provincial: 50-74
    const remaining = thresholdKm - 15;
    const factor = remaining > 0 ? (distanceKm - 15) / remaining : 0;
    score = 74 - factor * 24;
    level = 'BAJA';
  } else {
    // Fuera del umbral: 0-49
    score = Math.max(0, 49 - (distanceKm - thresholdKm) * 2);
    level = 'RECHAZADA';
  }
  
  return {
    score: Math.round(score * 10) / 10,
    distanceKm: Math.round(distanceKm * 100) / 100,
    withinThreshold,
    thresholdKm,
    provincia,
    level,
  };
}

/**
 * Verifica si un valor Y parece estar truncado
 * Útil para decisiones rápidas sin corrección completa
 * 
 * @param y - Coordenada Y a verificar
 * @returns true si parece truncada
 */
export function isPotentiallyTruncated(y: number): boolean {
  return y < Y_TRUNCATION_THRESHOLD && y > 0;
}

/**
 * Verifica si un valor Y está en el rango válido de Andalucía
 * 
 * @param y - Coordenada Y a verificar
 * @returns true si está en rango válido
 */
export function isValidAndaluciaY(y: number): boolean {
  return y >= ANDALUCIA_Y_MIN && y <= ANDALUCIA_Y_MAX;
}

// ============================================================================
// EXPORTACIÓN
// ============================================================================

export default {
  detectTruncation,
  correctTruncation,
  validateAgainstCentroid,
  isPotentiallyTruncated,
  isValidAndaluciaY,
  // A02: Nuevas funciones
  detectProvinciaFromINE,
  getProvincialThreshold,
  validateCorrectionConfidence,
  // Constantes
  ANDALUCIA_Y_MIN,
  ANDALUCIA_Y_MAX,
  Y_TRUNCATION_THRESHOLD,
  DEFAULT_VALIDATION_RADIUS_KM,
  CODIGO_PROVINCIA,
  UMBRALES_PROVINCIA,
  UMBRAL_DEFAULT_KM,
};
